import type { DashboardUpcomingWorkdayScheduleItem, WorkerDayAvailabilityItem, WorkerDayLeaveItem } from '@/types'

// Pure schedule helpers for the Scheduling page (moved unchanged from pages/live/schedule-workers.tsx).

export const toInputDate = (date: Date) => {
  const tzOffset = date.getTimezoneOffset()
  const local = new Date(date.getTime() - tzOffset * 60 * 1000)
  return local.toISOString().slice(0, 10)
}

export type DayTimelineEntry = {
  id: string
  jobId: string | null
  title: string
  workerLabel: string
  workerId: string | null
  locationLabel: string | null
  startMinute: number
  endMinute: number
  timeLabel: string
  hasClash: boolean
  isOnLeave: boolean
}

export type DayTimelineWorkerRow = {
  key: string
  worker: string
  workerId: string | null
  laneCount: number
  entries: Array<DayTimelineEntry & { laneIndex: number }>
}

export const DAY_TIMELINE_TOTAL_MINUTES = 24 * 60
export const DAY_TIMELINE_LANE_HEIGHT = 26
export const DAY_TIMELINE_GRID_HOURS = 3
export const DAY_TIMELINE_GRID_COLUMNS = 24 / DAY_TIMELINE_GRID_HOURS

export function buildDayEntries(
  entries: DashboardUpcomingWorkdayScheduleItem[],
  selectedDate: string,
  leaveLookup: Set<string>,
): DayTimelineEntry[] {
  return entries
    .map((entry) => {
      const startDate = toTimelineDate(entry.planned_start_local || entry.planned_start)
      const endDate = toTimelineDate(entry.planned_end_local || entry.planned_end)
      const dayKey = resolveEntryDay(entry)

      if (dayKey !== selectedDate) {
        return null
      }

      const safeStartMinute = startDate ? (startDate.getHours() * 60 + startDate.getMinutes()) : 0
      const rawEndMinute = endDate ? (endDate.getHours() * 60 + endDate.getMinutes()) : (safeStartMinute + 60)
      const safeEndMinute = Math.max(safeStartMinute + 15, rawEndMinute)

      return {
        id: String(entry.id),
        jobId: entry.job_id == null ? null : String(entry.job_id),
        title: String(entry.job_title || 'Untitled job'),
        workerLabel: getWorkerDisplayName(entry),
        workerId: entry.worker_id == null ? null : String(entry.worker_id),
        locationLabel: entry.location_name ? String(entry.location_name) : null,
        startMinute: Math.max(0, Math.min(DAY_TIMELINE_TOTAL_MINUTES - 1, safeStartMinute)),
        endMinute: Math.max(1, Math.min(DAY_TIMELINE_TOTAL_MINUTES, safeEndMinute)),
        timeLabel: formatTimelineTimeWindow(startDate, endDate),
        hasClash: entry.status === 'clashed',
        isOnLeave: resolveWorkerOnLeave(leaveLookup, entry.worker_id ?? null, selectedDate),
      }
    })
    .filter((entry): entry is DayTimelineEntry => Boolean(entry))
    .sort((left, right) => left.startMinute - right.startMinute)
}

export function buildDayWorkerRows(
  entries: DayTimelineEntry[],
  workerIds: string[] = [],
  workerNameLookup: Map<string, string> = new Map(),
): DayTimelineWorkerRow[] {
  const byWorker = new Map<string, DayTimelineEntry[]>()
  const workerMeta = new Map<string, { worker: string; workerId: string | null }>()

  for (const entry of entries) {
    const workerId = entry.workerId ? String(entry.workerId) : null
    const workerKey = workerId ? `id:${workerId}` : `name:${entry.workerLabel}`
    const group = byWorker.get(workerKey) ?? []
    group.push(entry)
    byWorker.set(workerKey, group)
    workerMeta.set(workerKey, {
      worker: entry.workerLabel,
      workerId,
    })
  }

  for (const workerId of workerIds) {
    const workerKey = `id:${workerId}`
    if (byWorker.has(workerKey)) {
      continue
    }

    byWorker.set(workerKey, [])
    workerMeta.set(workerKey, {
      worker: workerNameLookup.get(workerId) || `Worker #${workerId}`,
      workerId,
    })
  }

  return Array.from(byWorker.entries())
    .sort(([leftKey], [rightKey]) => {
      const leftMeta = workerMeta.get(leftKey)
      const rightMeta = workerMeta.get(rightKey)
      const leftWorker = leftMeta?.worker || leftKey
      const rightWorker = rightMeta?.worker || rightKey
      return leftWorker.localeCompare(rightWorker)
    })
    .map(([workerKey, workerEntries]) => {
      const meta = workerMeta.get(workerKey)
      const sorted = [...workerEntries].sort((left, right) => left.startMinute - right.startMinute)
      const laneEnds: number[] = []

      const withLanes = sorted.map((entry) => {
        let laneIndex = laneEnds.findIndex((endMinute) => entry.startMinute >= endMinute)
        if (laneIndex === -1) {
          laneIndex = laneEnds.length
          laneEnds.push(entry.endMinute)
        } else {
          laneEnds[laneIndex] = entry.endMinute
        }

        return {
          ...entry,
          laneIndex,
        }
      })

      return {
        key: workerKey,
        worker: meta?.worker || withLanes[0]?.workerLabel || 'Unknown worker',
        workerId: meta?.workerId ?? withLanes[0]?.workerId ?? null,
        laneCount: Math.max(1, laneEnds.length),
        entries: withLanes,
      }
    })
}

export function toTimelineDate(value?: string | null) {
  if (!value) {
    return null
  }

  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function formatTimelineTimeWindow(startDate: Date | null, endDate: Date | null) {
  if (!startDate && !endDate) {
    return '00:00 - 23:59'
  }

  const startLabel = startDate
    ? `${String(startDate.getHours()).padStart(2, '0')}:${String(startDate.getMinutes()).padStart(2, '0')}`
    : '00:00'
  const endLabel = endDate
    ? `${String(endDate.getHours()).padStart(2, '0')}:${String(endDate.getMinutes()).padStart(2, '0')}`
    : '23:59'

  return `${startLabel} - ${endLabel}`
}

export function buildWeeklyRows(
  entries: DashboardUpcomingWorkdayScheduleItem[],
  sevenDays: string[],
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
  workerNameLookup: Map<string, string>,
) {
  const byWorker = new Map<string, {
    key: string
    worker: string
    workerId: string | number | null
    rows: DashboardUpcomingWorkdayScheduleItem[]
  }>()
  const workerNameById = new Map<string, string>()

  entries.forEach((entry) => {
    const workerId = entry.worker_id ?? null
    const worker = getWorkerDisplayName(entry).replace('Unassigned worker', 'Unassigned')
    const key = workerId != null ? `id:${String(workerId)}` : `name:${worker}`
    if (workerId != null && worker && worker !== 'Unassigned') {
      workerNameById.set(String(workerId), worker)
    }
    const group = byWorker.get(key)

    if (group) {
      group.rows.push(entry)
      return
    }

    byWorker.set(key, {
      key,
      worker,
      workerId,
      rows: [entry],
    })
  })

  const workerIdsInWindow = collectWorkerIdsForDays(availabilityLookup, leaveLookup, sevenDays)
  workerIdsInWindow.forEach((workerId) => {
    const key = `id:${workerId}`
    if (byWorker.has(key)) {
      return
    }

    byWorker.set(key, {
      key,
      worker: workerNameById.get(workerId) || workerNameLookup.get(workerId) || `Worker #${workerId}`,
      workerId,
      rows: [],
    })
  })

  return Array.from(byWorker.values()).map(({ key, worker, workerId, rows }) => {
    const scheduled = sevenDays.map((day, dayIndex) => {
      const availabilityText = resolveWorkerDayAvailability(availabilityLookup, workerId, day)
      const isOnLeave = resolveWorkerOnLeave(leaveLookup, workerId, day)
      const items = rows.filter((entry) => resolveEntryDay(entry) === day)
      if (items.length === 0) {
        const isUnavailable = availabilityText === 'Unavailable' || isOnLeave
        return {
          key: `${worker}-${day}-available`,
          time: '',
          title: '',
          availabilityLabel: isOnLeave ? 'On Leave' : (isUnavailable ? 'Unavailable' : 'Available'),
          availabilityText: isOnLeave ? 'On Leave' : availabilityText,
          isAvailable: true,
          isOutsideAvailability: false,
          isOnLeave,
          tone: isUnavailable ? 'bg-[#fde9eb]' : 'bg-[#ddf6e7]',
        }
      }

      const primary = pickDisplayEntry(items)
      const time = formatEntryTimeRange(primary)
      const isOutsideAvailability = isOnLeave || isScheduleOutsideAvailabilityWindow(time, availabilityText)
      const extraLabel = items.length > 1 ? ` +${items.length - 1}` : ''
      return {
        key: `${worker}-${day}-${dayIndex}`,
        jobId: primary.job_id,
        time,
        title: `${primary.job_title || 'Scheduled work'}${extraLabel}`,
        availabilityLabel: '',
        availabilityText: isOnLeave ? 'On Leave' : availabilityText,
        isAvailable: false,
        isOutsideAvailability,
        isOnLeave,
        tone: isOutsideAvailability ? 'bg-[#cb3d4d]' : resolveScheduleTone(primary),
      }
    })

    return {
      key,
      worker,
      workerId,
      role: 'Field Worker',
      cells: scheduled,
      notes: rows.length === 0
        ? 'No assignments yet'
        : (rows.some((entry) => entry.status === 'clashed') ? 'Potential clash detected' : 'Ready for scheduling'),
    }
  })
}

export function collectWorkerIdsForDays(
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
  days: string[],
) {
  const daySet = new Set(days)
  const workerIds = new Set<string>()

  availabilityLookup.forEach((_value, key) => {
    const [workerId, day] = key.split('|')
    if (!workerId || !daySet.has(day)) {
      return
    }
    workerIds.add(workerId)
  })

  leaveLookup.forEach((key) => {
    const [workerId, day] = key.split('|')
    if (!workerId || !daySet.has(day)) {
      return
    }
    workerIds.add(workerId)
  })

  return Array.from(workerIds).sort((a, b) => a.localeCompare(b))
}

export function collectWorkerIdsForSingleDay(
  availabilityLookup: Map<string, string>,
  leaveLookup: Set<string>,
  day: string,
) {
  if (!day) {
    return []
  }

  return collectWorkerIdsForDays(availabilityLookup, leaveLookup, [day])
}

export function buildWorkerNameLookup(
  schedules: DashboardUpcomingWorkdayScheduleItem[],
  availabilityItems: WorkerDayAvailabilityItem[],
  leaveItems: WorkerDayLeaveItem[],
) {
  const lookup = new Map<string, string>()

  const pushName = (workerId: number | string | null | undefined, workerName: string | null | undefined) => {
    if (workerId == null) {
      return
    }

    const id = String(workerId).trim()
    const name = String(workerName || '').trim()
    if (!id || !name) {
      return
    }

    lookup.set(id, name)
  }

  schedules.forEach((entry) => {
    pushName(entry.worker_id ?? null, entry.worker_name ?? null)
  })

  availabilityItems.forEach((entry) => {
    pushName(entry.worker_id ?? null, entry.worker_name ?? null)
  })

  leaveItems.forEach((entry) => {
    pushName(entry.worker_id ?? null, entry.worker_name ?? null)
  })

  return lookup
}

export function buildWorkerAvailabilityLookup(items: WorkerDayAvailabilityItem[]) {
  const lookup = new Map<string, string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate) {
      return
    }

    const key = `${workerId}|${workDate}`
    if (item.is_available === false) {
      lookup.set(key, '--:--')
      return
    }

    const from = String(item.from_time || '').trim()
    const to = String(item.to_time || '').trim()
    if (!from || !to) {
      if (!lookup.has(key)) {
        lookup.set(key, '--:--')
      }
      return
    }

    const nextValue = `${formatHourMinute(from)}-${formatHourMinute(to)}`
    const existing = lookup.get(key)
    if (!existing || existing === 'Available') {
      lookup.set(key, nextValue)
      return
    }
    if (existing !== 'Unavailable') {
      lookup.set(key, `${existing}, ${nextValue}`)
    }
  })

  return lookup
}

export function buildWorkerLeaveLookup(items: WorkerDayLeaveItem[]) {
  const lookup = new Set<string>()

  items.forEach((item) => {
    const workerId = item.worker_id == null ? '' : String(item.worker_id).trim()
    const workDate = String(item.work_date || '').trim()
    if (!workerId || !workDate || item.is_on_leave !== true) {
      return
    }

    lookup.add(`${workerId}|${workDate}`)
  })

  return lookup
}

export function resolveWorkerOnLeave(lookup: Set<string>, workerId: number | string | null, day: string) {
  if (workerId == null) {
    return false
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.has(key)
}

export function isScheduleOutsideAvailabilityWindow(scheduleTime: string, availabilityText: string) {
  const scheduleRange = parseHourMinuteRange(scheduleTime)
  if (!scheduleRange) {
    return false
  }

  const availabilityRaw = String(availabilityText || '').trim()
  if (!availabilityRaw || availabilityRaw === '--:--') {
    return false
  }

  if (availabilityRaw.toLowerCase() === 'unavailable') {
    return true
  }

  const availabilityRanges = availabilityRaw
    .split(',')
    .map((chunk) => parseHourMinuteRange(chunk.trim()))
    .filter((range): range is [number, number] => range != null)

  if (availabilityRanges.length === 0) {
    return false
  }

  return !availabilityRanges.some(([availableStart, availableEnd]) => {
    const [scheduledStart, scheduledEnd] = scheduleRange
    return scheduledStart >= availableStart && scheduledEnd <= availableEnd
  })
}

export function parseHourMinuteRange(value: string): [number, number] | null {
  const raw = String(value || '').trim()
  const [startRaw, endRaw] = raw.split('-').map((part) => part.trim())
  if (!startRaw || !endRaw) {
    return null
  }

  const start = parseHourMinuteToMinutes(startRaw)
  const end = parseHourMinuteToMinutes(endRaw)
  if (start == null || end == null || end <= start) {
    return null
  }

  return [start, end]
}

export function parseAvailabilityRanges(availabilityText: string): Array<[number, number]> {
  const raw = String(availabilityText || '').trim()
  if (!raw || raw === '--:--' || raw.toLowerCase() === 'unavailable' || raw.toLowerCase() === 'on leave') {
    return []
  }

  return raw
    .split(',')
    .map((chunk) => parseHourMinuteRange(chunk.trim()))
    .filter((range): range is [number, number] => range != null)
}

export function formatTimelineMinuteRange(startMinute: number, endMinute: number) {
  const startHour = Math.floor(startMinute / 60)
  const startMin = startMinute % 60
  const endHour = Math.floor(endMinute / 60)
  const endMin = endMinute % 60
  return `${String(startHour).padStart(2, '0')}:${String(startMin).padStart(2, '0')} - ${String(endHour).padStart(2, '0')}:${String(endMin).padStart(2, '0')}`
}

export function parseHourMinuteToMinutes(value: string): number | null {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/)
  if (!match) {
    return null
  }

  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (!Number.isInteger(hours) || !Number.isInteger(minutes)) {
    return null
  }
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    return null
  }

  return hours * 60 + minutes
}

export function formatHourMinute(value: string) {
  const raw = String(value || '').trim()
  const match = raw.match(/^(\d{1,2}):(\d{2})/)
  if (!match) {
    return raw
  }
  return `${match[1].padStart(2, '0')}:${match[2]}`
}

export function resolveWorkerDayAvailability(lookup: Map<string, string>, workerId: number | string | null, day: string) {
  if (workerId == null) {
    return '--:--'
  }
  const key = `${String(workerId).trim()}|${day}`
  return lookup.get(key) || '--:--'
}

export function buildSevenDayWindow(startDate: string) {
  const start = new Date(startDate)
  if (Number.isNaN(start.getTime())) {
    return []
  }

  const days: string[] = []
  for (let index = 0; index < 7; index += 1) {
    const day = new Date(start)
    day.setDate(start.getDate() + index)
    days.push(toInputDate(day))
  }
  return days
}

export function resolveEntryDay(entry: DashboardUpcomingWorkdayScheduleItem) {
  const explicitDay = String(entry.work_date || '').trim()
  if (/^\d{4}-\d{2}-\d{2}$/.test(explicitDay)) {
    return explicitDay
  }

  // Fall back to planned_start UTC day when work_date is unavailable.
  const plannedStart = String(entry.planned_start || '').trim()
  if (!plannedStart) {
    return ''
  }

  if (/^\d{4}-\d{2}-\d{2}/.test(plannedStart)) {
    return plannedStart.slice(0, 10)
  }

  const parsed = new Date(plannedStart)
  if (Number.isNaN(parsed.getTime())) {
    return ''
  }
  return parsed.toISOString().slice(0, 10)
}

export function getWorkerDisplayName(entry: DashboardUpcomingWorkdayScheduleItem) {
  const name = String(entry.worker_name || '').trim()
  if (name) {
    return name
  }

  if (entry.worker_id != null && String(entry.worker_id).trim()) {
    return `Worker #${String(entry.worker_id).trim()}`
  }

  return 'Unassigned worker'
}

export function formatEntryTimeRange(entry: DashboardUpcomingWorkdayScheduleItem) {
  const normalized = formatRangeFromDateTimes(entry.planned_start, entry.planned_end)
  if (normalized) {
    return normalized
  }

  const start = entry.planned_start_local || entry.planned_start
  const end = entry.planned_end_local || entry.planned_end
  return `${toShortTime(start)}-${toShortTime(end)}`
}

export function toShortTime(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw) {
    return '--'
  }

  // Date-only values do not include a schedule time; avoid coercing to midnight.
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return '--'
  }

  const directMatch = raw.match(/\b(\d{2}:\d{2})/)
  if (directMatch?.[1]) {
    return directMatch[1]
  }

  const parsed = new Date(raw)
  if (Number.isNaN(parsed.getTime())) {
    return raw
  }

  return parsed.toISOString().slice(11, 16)
}

export function pickDisplayEntry(items: DashboardUpcomingWorkdayScheduleItem[]) {
  const withNonMidnight = items.find((item) => {
    const label = formatEntryTimeRange(item)
    return label !== '00:00-00:00' && label !== '-----'
  })
  return withNonMidnight || items[0]
}

export function formatRangeFromDateTimes(startValue?: string | null, endValue?: string | null) {
  const start = parseScheduleDate(startValue)
  const end = parseScheduleDate(endValue)
  if (!start || !end) {
    return null
  }

  const startMs = start.getTime()
  const endMs = end.getTime()
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    return null
  }

  const formatter = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })

  return `${formatter.format(start)}-${formatter.format(end)}`
}

export function parseScheduleDate(value?: string | null) {
  const raw = String(value || '').trim()
  if (!raw || /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return null
  }

  const parsed = new Date(raw)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

export function collectWorkerCandidates(rows: ReturnType<typeof buildWeeklyRows>) {
  return rows
    .filter((row) => row.worker && row.worker !== 'Unassigned')
    .map((row) => ({
      name: row.worker,
      workerId: row.workerId,
      hasOutsideAvailability: row.cells.some((cell) => cell.isOutsideAvailability),
      hasLeave: row.cells.some((cell) => cell.isOnLeave),
    }))
    .slice(0, 4)
}

export function formatCompactDayLabel(value: string) {
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
  }).replace(',', '')
}

export function resolveScheduleTone(entry: DashboardUpcomingWorkdayScheduleItem) {
  const hasAssignedWorker = entry.worker_id != null || Boolean(String(entry.worker_name || '').trim())
  if (!hasAssignedWorker) {
    return 'bg-[#cb3d4d]'
  }

  if (entry.status === 'clashed') {
    return 'bg-[#cf8a1a]'
  }

  return 'bg-[#2f8fdd]'
}

export type WeeklyRow = ReturnType<typeof buildWeeklyRows>[number]
export type WeeklyCell = WeeklyRow['cells'][number]
